import './RoleStageEligibility.scss';
import { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Loader as SpinnerLoader,
  SelectionTile,
  TNotification,
  useNotification,
} from '@spotted-zebra-uk/ui-components';
import Loader from 'components/atoms/Loader/Loader';
import { useQuestionnaireSubmitResponsesMutation } from 'generated/graphql';
import FactCheckIcon from 'assets/icons/fact_check.svg?react';
import RoleStageEligibilityLayout from './RoleStageEligibilityLayout';
import { useEligibilityStage } from './useEligibilityStage';

// The verdict is instant server-side; hold the page briefly after a
// successful submit so the candidate sees their answers being "calculated"
// before the portal shows the outcome.
const CALCULATING_DELAY_MS = 5000;

const RoleStageEligibility: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { handleMsgType } = useNotification();
  const { role, stage, loading, backUrl } = useEligibilityStage();
  const [answers, setAnswers] = useState<Record<string, boolean>>({});
  const [isCalculating, setIsCalculating] = useState(false);
  const calculatingTimerRef = useRef<ReturnType<typeof setTimeout>>();

  const [submitResponses, { loading: isSubmitting }] =
    useQuestionnaireSubmitResponsesMutation({
      onCompleted: () => {
        setIsCalculating(true);
        calculatingTimerRef.current = setTimeout(() => {
          navigate(backUrl);
        }, CALCULATING_DELAY_MS);
      },
      onError: error => {
        handleMsgType({
          type: TNotification.error,
          message: error?.message,
        });
      },
    });

  // Don't navigate from an unmounted page (e.g. the candidate hit back).
  useEffect(
    () => () => {
      if (calculatingTimerRef.current) {
        clearTimeout(calculatingTimerRef.current);
      }
    },
    []
  );

  if (loading || !role || !stage) {
    return <Loader isOverlay />;
  }

  const allAnswered = stage.items.every(
    item => typeof answers[item.id] === 'boolean'
  );

  const handleSubmitClick = () => {
    if (!allAnswered) {
      handleMsgType({
        type: TNotification.error,
        message: t('eligibilityStage.answerAllQuestions'),
      });
      return;
    }
    submitResponses({
      variables: {
        shortlistStageCandidateId: stage.shortlistStageCandidateId,
        responses: stage.items.map(item => ({
          itemId: item.id,
          answer: answers[item.id],
        })),
      },
    });
  };

  return (
    <RoleStageEligibilityLayout
      role={role}
      pageTitle={stage.name}
      backUrl={backUrl}
    >
      {isCalculating ? (
        // Post-submit hold: the questions make way for a centred spinner and
        // message until the portal shows the verdict.
        <main
          className="RoleStageEligibility__Calculating"
          data-testid="eligibility-stage-calculating"
        >
          <SpinnerLoader variant="spinner" size="medium" />
          <p className="RoleStageEligibility__Calculating__Text">
            {t('eligibilityStage.reviewingResponses')}
          </p>
        </main>
      ) : (
        <main className="RoleStageEligibility__Main">
          <FactCheckIcon
            className="RoleStageEligibility__Icon"
            aria-hidden="true"
          />
          <h1 className="RoleStageEligibility__Title">{stage.name}</h1>
          <p className="RoleStageEligibility__Intro">
            {t('eligibilityStage.intro')}
          </p>
          <div className="RoleStageEligibility__Questions">
            {stage.items.map(item => (
              <fieldset
                key={item.id}
                className="RoleStageEligibility__Question"
              >
                <legend>{item.text}</legend>
                <div className="RoleStageEligibility__Answers">
                  {[true, false].map(answerValue => (
                    <SelectionTile
                      key={String(answerValue)}
                      id={`eligibility-answer-${item.id}-${answerValue}`}
                      name={`eligibility-answer-${item.id}`}
                      value={String(answerValue)}
                      checked={answers[item.id] === answerValue}
                      onChange={() =>
                        setAnswers(previous => ({
                          ...previous,
                          [item.id]: answerValue,
                        }))
                      }
                    >
                      {answerValue
                        ? t('eligibilityStage.yes')
                        : t('eligibilityStage.no')}
                    </SelectionTile>
                  ))}
                </div>
              </fieldset>
            ))}
          </div>
          <div className="RoleStageEligibility__Footer">
            <Button
              onClick={handleSubmitClick}
              loading={isSubmitting || isCalculating}
              disabled={!allAnswered}
              className="RoleStageEligibility__SubmitButton"
              data-testid="eligibility-stage-submit-button"
            >
              {isCalculating
                ? t('eligibilityStage.calculating')
                : t('eligibilityStage.submit')}
            </Button>
          </div>
        </main>
      )}
    </RoleStageEligibilityLayout>
  );
};

export default RoleStageEligibility;
